import React from "react";

const TermsOfService: React.FC = () => {
  const useScrollToTop = () => {
    React.useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  }
  useScrollToTop();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Please read these terms carefully before using Formatally. 
              By accessing our service, you agree to be bound by these terms.
            </p>
            <div className="mt-4 text-sm text-gray-500">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          {/* Quick Summary Box */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Quick Summary</h3>
            <p className="text-blue-800">
              By using Formatally, you agree to use our service responsibly, respect intellectual property rights, 
              and understand that we provide our service "as-is" with certain limitations on liability.
            </p>
          </div>

          {/* Terms Content */}
          <div className="space-y-8">
            
            {/* Section 1 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                1. Acceptance of Terms
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  By accessing, browsing, or using the Formatally website ("Service"), you acknowledge 
                  that you have read, understood, and agree to be bound by these Terms of Service 
                  ("Terms") and our Privacy Policy. If you do not agree to these Terms, you must 
                  discontinue use of the Service immediately.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  These Terms constitute a legally binding agreement between you and Formatally. 
                  Your continued use of the Service signifies your acceptance of any modifications 
                  to these Terms.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                2. About Formatally
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Formatally is a web-based service operated by an individual entrepreneur based in India. 
                  We provide formatting and document processing tools to help users create, edit, and 
                  manage their digital content efficiently.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Contact Information:</strong></p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:contact@formatally.com" className="text-blue-600 hover:text-blue-800 underline">contact@formatally.com</a>
                  </p>
                  <p className="text-gray-700">
                    Website: <a href="https://www.formatally.com" className="text-blue-600 hover:text-blue-800 underline">www.formatally.com</a>
                  </p>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                3. Permitted Use and Restrictions
              </h2>
              <div className="prose prose-gray max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Permitted Uses:</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                  <li>Personal and commercial document formatting</li>
                  <li>Educational and research purposes</li>
                  <li>Creating professional documents and presentations</li>
                  <li>Processing publicly available content you own or have rights to use</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Prohibited Activities:</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                  <li>Violating any applicable laws or regulations</li>
                  <li>Infringing on intellectual property rights</li>
                  <li>Attempting to reverse engineer, hack, or compromise our systems</li>
                  <li>Uploading malicious content, viruses, or harmful code</li>
                  <li>Using the service to harass, abuse, or harm others</li>
                  <li>Creating fake accounts or impersonating others</li>
                  <li>Excessive automated requests that may overload our servers</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                4. User Accounts and Registration
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Some features of our Service may require you to create an account. You are responsible 
                  for maintaining the confidentiality of your account credentials and for all activities 
                  that occur under your account.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You must provide accurate and complete information when creating an account and keep 
                  your information updated. You must immediately notify us of any unauthorized use of 
                  your account.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                5. Intellectual Property Rights
              </h2>
              <div className="prose prose-gray max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Content:</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All content, features, and functionality of the Service, including but not limited to 
                  text, graphics, logos, button icons, images, audio clips, digital downloads, data 
                  compilations, and software, are owned by Formatally and protected by international 
                  copyright, trademark, patent, trade secret, and other intellectual property laws.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Content:</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You retain ownership of any content you submit, upload, or process through our Service. 
                  However, by using our Service, you grant us a limited, non-exclusive, royalty-free 
                  license to process, store, and display your content solely for the purpose of providing 
                  the Service to you.
                </p>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                  <p className="text-amber-800">
                    <strong>Important:</strong> We do not claim ownership of your content and will not 
                    use it for any purpose other than providing our services to you.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                6. Privacy and Data Protection
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Your privacy is important to us. Our collection, use, and protection of your personal 
                  information is governed by our Privacy Policy, which is incorporated into these Terms 
                  by reference. By using our Service, you consent to the collection and use of your 
                  information as outlined in our Privacy Policy.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We implement appropriate security measures to protect your personal information, but 
                  no method of transmission over the Internet is 100% secure. We cannot guarantee 
                  absolute security of your data.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                7. Service Availability and Modifications
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We strive to maintain high availability of our Service, but we do not guarantee 
                  uninterrupted access. The Service may be temporarily unavailable due to maintenance, 
                  updates, or circumstances beyond our control.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify, suspend, or discontinue any aspect of the Service 
                  at any time, with or without notice. We may also impose limits on certain features 
                  or restrict access to parts of the Service.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                8. Disclaimers and Warranties
              </h2>
              <div className="prose prose-gray max-w-none">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg mb-4">
                  <p className="text-red-800 font-medium">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We disclaim all warranties, express or implied, including but not limited to 
                  warranties of merchantability, fitness for a particular purpose, and non-infringement. 
                  We do not warrant that the Service will be error-free, secure, or continuously available.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You acknowledge that your use of the Service is at your own risk, and you are solely 
                  responsible for any damage to your computer system or loss of data that results from 
                  such use.
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                9. Limitation of Liability
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  To the maximum extent permitted by applicable law, Formatally shall not be liable 
                  for any indirect, incidental, special, consequential, or punitive damages, including 
                  but not limited to loss of profits, data, use, goodwill, or other intangible losses.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Our total liability to you for all claims arising out of or relating to these Terms 
                  or your use of the Service shall not exceed the amount you paid us in the twelve 
                  months preceding the claim, or $100, whichever is greater.
                </p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                10. Indemnification
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Formatally from and against any 
                  and all claims, damages, obligations, losses, liabilities, costs, and expenses 
                  (including attorney's fees) arising from your use of the Service, your violation 
                  of these Terms, or your infringement of any third-party rights.
                </p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                11. Third-Party Services and Links
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our Service may contain links to third-party websites, applications, or services 
                  that are not owned or controlled by Formatally. We have no control over and assume 
                  no responsibility for the content, privacy policies, or practices of any third-party services.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You acknowledge and agree that we shall not be responsible or liable for any damage 
                  or loss caused by your use of any third-party services, content, goods, or services 
                  available through such links.
                </p>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                12. Termination
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  You may stop using our Service at any time. We may suspend or terminate your access 
                  to the Service at any time, with or without cause or notice, including if we believe 
                  you have violated these Terms.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Upon termination, your right to use the Service will cease immediately, but the 
                  provisions of these Terms that by their nature should survive termination will 
                  remain in effect.
                </p>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                13. Dispute Resolution
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We encourage you to contact us first to resolve any disputes informally. If we 
                  cannot resolve a dispute through informal means, any legal action must be filed 
                  within one year of the date the dispute arose.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  These Terms and any disputes will be governed by the laws of India, without regard 
                  to conflict of law principles. Any legal proceedings shall be subject to the 
                  exclusive jurisdiction of the courts in India.
                </p>
              </div>
            </section>

            {/* Section 14 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                14. Changes to Terms
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to modify these Terms at any time. We will notify users of 
                  significant changes by posting the updated Terms on our website and updating the 
                  "Last updated" date. For material changes, we may also send notifications via email.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Your continued use of the Service after any changes constitutes acceptance of the 
                  new Terms. If you do not agree to the modified Terms, you must stop using the Service.
                </p>
              </div>
            </section>

            {/* Section 15 */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                15. General Provisions
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  If any provision of these Terms is found to be unenforceable, the remaining 
                  provisions will remain in full force and effect. Our failure to enforce any 
                  provision of these Terms does not constitute a waiver of such provision.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  These Terms constitute the entire agreement between you and Formatally regarding 
                  the Service and supersede all prior agreements and understandings.
                </p>
              </div>
            </section>

          </div>

          {/* Contact Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Questions About These Terms?</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms of Service or need clarification on any 
                provision, please don't hesitate to contact us.
              </p>
              <div className="flex items-center space-x-4">
                <a 
                  href="mailto:contact@formatally.com" 
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Contact Support
                </a>
                <span className="text-gray-500">or</span>
                <a 
                  href="https://www.formatally.com" 
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Visit our website
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermsOfService;